import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  Alert,
  Button,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconCoins,
  IconInfoCircle,
  IconPackage,
  IconShoppingCart,
  IconCategory,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { StatCard } from "../../components/StatCard";
import { DetailPageSkeleton } from "../../components/SkeletonLoader";
import {
  useOrganizationLimits,
  getProgressColor,
  formatLimitUsage,
} from "../../../../business/limits.hook";

const GAMIFICATION_STATS_QUERY = gql`
  query GamificationStats {
    currencies(page: { first: 1 }) {
      totalCount
    }
    items(page: { first: 1 }) {
      totalCount
    }
    itemCategories(page: { first: 1 }) {
      totalCount
    }
    shops(page: { first: 1 }) {
      totalCount
    }
  }
`;

export function GamificationHome() {
  const navigate = useNavigate();
  const { data, loading } = useQuery<any>(GAMIFICATION_STATS_QUERY);
  const { getOrganizationLimit } = useOrganizationLimits([
    "CURRENCIES",
    "ITEMS",
    "ITEM_CATEGORIES",
    "SHOPS",
  ]);

  if (loading) {
    return <DetailPageSkeleton />;
  }

  const currenciesCount = data?.currencies?.totalCount ?? 0;
  const itemsCount = data?.items?.totalCount ?? 0;
  const categoriesCount = data?.itemCategories?.totalCount ?? 0;
  const shopsCount = data?.shops?.totalCount ?? 0;

  const currenciesLimit = getOrganizationLimit("CURRENCIES");
  const itemsLimit = getOrganizationLimit("ITEMS");
  const categoriesLimit = getOrganizationLimit("ITEM_CATEGORIES");
  const shopsLimit = getOrganizationLimit("SHOPS");

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Gamification Dashboard"
        subtitle="Manage currencies, items, and shops for your gamification system"
        showBreadcrumbs={false}
      />

      {/* Stat Cards */}
      <SimpleGrid
        cols={{ base: 2, md: 4 }}
        className="wp-stagger-children"
        mb="lg"
      >
        <StatCard
          label="Currencies"
          value={currenciesCount}
          iconType="currency"
          description="Virtual currencies available"
          onClick={() => navigate("currencies")}
          tooltip="Click to manage currencies"
          progress={currenciesLimit?.pct}
          progressLabel={
            currenciesLimit ? formatLimitUsage(currenciesLimit) : undefined
          }
          progressColor={
            currenciesLimit ? getProgressColor(currenciesLimit.pct) : undefined
          }
        />
        <StatCard
          label="Items"
          value={itemsCount}
          iconType="stats"
          icon={<IconPackage size={16} stroke={1.5} />}
          description="Items in catalog"
          onClick={() => navigate("items-catalog/items")}
          tooltip="Click to manage items"
          progress={itemsLimit?.pct}
          progressLabel={itemsLimit ? formatLimitUsage(itemsLimit) : undefined}
          progressColor={
            itemsLimit ? getProgressColor(itemsLimit.pct) : undefined
          }
        />
        <StatCard
          label="Categories"
          value={categoriesCount}
          iconType="stats"
          icon={<IconCategory size={16} stroke={1.5} />}
          description="Item categories"
          onClick={() => navigate("items-catalog/categories")}
          tooltip="Click to manage categories"
          progress={categoriesLimit?.pct}
          progressLabel={
            categoriesLimit ? formatLimitUsage(categoriesLimit) : undefined
          }
          progressColor={
            categoriesLimit ? getProgressColor(categoriesLimit.pct) : undefined
          }
        />
        <StatCard
          label="Shops"
          value={shopsCount}
          iconType="stats"
          icon={<IconShoppingCart size={16} stroke={1.5} />}
          description="Active shops"
          onClick={() => navigate("shops")}
          tooltip="Click to manage shops"
          progress={shopsLimit?.pct}
          progressLabel={shopsLimit ? formatLimitUsage(shopsLimit) : undefined}
          progressColor={
            shopsLimit ? getProgressColor(shopsLimit.pct) : undefined
          }
        />
      </SimpleGrid>

      {/* Quick Actions */}
      <Group mb="lg">
        <Button
          leftSection={<IconCoins size={16} />}
          variant="light"
          onClick={() => navigate("currencies")}
          className="wp-btn-press"
        >
          Manage Currencies
        </Button>
        <Button
          leftSection={<IconPackage size={16} />}
          variant="light"
          onClick={() => navigate("items-catalog/items")}
          className="wp-btn-press"
        >
          Manage Items
        </Button>
        <Button
          leftSection={<IconShoppingCart size={16} />}
          variant="light"
          onClick={() => navigate("shops")}
          className="wp-btn-press"
        >
          Manage Shops
        </Button>
      </Group>

      {/* Documentation */}
      <Stack mt="xl">
        <Text>
          The Gamification service lets you create virtual economies for your
          platform. Define currencies, build item catalogs, and set up shops
          where players can spend their earnings. Everything is accessible
          through the API, so you can integrate rewards, purchases, and
          inventories into your game or application.
        </Text>

        <Text>
          <Title order={2}>Currencies</Title>
          Currencies are the foundation of your virtual economy. Each currency
          has a name, symbol, and optional description.
          <br />
          Players earn currencies through events, tournament placements,
          achievements, or direct admin adjustments.
          <br />
          You can create as many currencies as needed — for example, a primary
          &quot;Gold&quot; currency for general purchases and a premium
          &quot;Gems&quot; currency for exclusive items.
          <br />
          <br />
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Currencies can be marked as hidden to keep them invisible to players
            while still being usable through the API. This is useful for
            internal tracking currencies or currencies not yet ready for launch.
          </Alert>
        </Text>

        <Text>
          <Title order={2}>Items &amp; Categories</Title>
          Items represent anything that can be owned by a player — cosmetics,
          consumables, power-ups, badges, or any custom concept your platform
          needs.
          <br />
          Each item belongs to a category, which helps organize your catalog and
          enables filtering in shops.
          <br />
          Items and categories both support metadata, allowing you to attach
          custom key-value pairs for any additional data your application
          requires (e.g., rarity, image URLs, stat modifiers).
          <br />
          <br />
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Use External IDs on items and categories to link them to entities in
            your own system. This makes it easy to synchronize data between
            WellPlayed and your game backend.
          </Alert>
        </Text>

        <Text>
          <Title order={2}>Shops</Title>
          Shops are storefronts where players can browse and purchase products
          using their currencies.
          <br />
          Each shop can have its own set of products and categories to organize
          them.
          <br />
          You can create multiple shops for different purposes — a main shop, a
          seasonal event shop, a VIP-only shop, etc.
          <br />
          <br />
          Shop products define what is available for purchase, including:
          <List>
            <List.Item>
              The item being sold and the quantity per purchase
            </List.Item>
            <List.Item>The price in one or more currencies</List.Item>
            <List.Item>
              Optional visibility date to schedule when a product becomes
              available
            </List.Item>
            <List.Item>
              Optional external ID for integration with your own systems
            </List.Item>
          </List>
        </Text>

        <Text>
          <Title order={2}>Purchase Flow</Title>
          When a player purchases a product from a shop, the system
          automatically:
          <List type="ordered">
            <List.Item>
              Verifies the player has enough currency to cover the price
            </List.Item>
            <List.Item>
              Deducts the currency amount from the player&apos;s balance
            </List.Item>
            <List.Item>
              Adds the purchased item(s) to the player&apos;s inventory
            </List.Item>
            <List.Item>
              Records the transaction in the purchase history
            </List.Item>
          </List>
          <br />
          All purchases are tracked and can be reviewed in the Purchase History
          section of each shop.
          <br />
          <br />
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            The purchase flow is fully atomic — if any step fails (insufficient
            funds, item unavailable), the entire transaction is rolled back and
            no changes are applied.
          </Alert>
        </Text>

        <Text>
          <Title order={2}>Metadata &amp; External IDs</Title>
          Most gamification entities (currencies, items, categories, shops,
          products) support two integration features:
          <List>
            <List.Item>
              <strong>Metadata</strong>: Arbitrary key-value pairs attached to
              an entity. Use metadata to store custom data like image URLs,
              color codes, localization keys, or game-specific attributes.
            </List.Item>
            <List.Item>
              <strong>External ID</strong>: A single string identifier to link
              the entity to a corresponding record in your own database or
              backend system.
            </List.Item>
          </List>
          <br />
          Both metadata and external IDs are returned in API responses and can
          be used for filtering, making it easy to build rich integrations on
          top of the gamification system.
        </Text>
      </Stack>
    </div>
  );
}
